import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Typography,
  Button,
  Grid,
  Icon,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlans } from '../../shared/hooks/usePlans';
import type { IBusinessView, PlanListView } from '../../shared/services';

const planColors: Record<string, string> = {
  solo: '#4CAF50',
  studio: '#1976D2',
  elite: '#7B1FA2',
};

const categoryLabels: Record<string, string> = {
  core: 'Gestão',
  financial: 'Financeiro',
  analytics: 'Analytics',
  presence: 'Presença Digital',
  whatsapp: 'WhatsApp',
  admin: 'Administração',
  marketing: 'Marketing',
  support: 'Suporte',
};

interface Props {
  business: IBusinessView;
}

export const MyPlan: React.FC<Props> = ({ business }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>(
    business.subscription?.billingCycle === 'Yearly' ? 'annual' : 'monthly'
  );
  const { plans, isLoading: isLoadingPlans } = usePlans();

  const subscription = business.subscription;

  const currentPlan = plans?.find(
    (p) => p.id === subscription?.subscriptionPlanId
  );

  const upgradePlans = plans?.filter(
    (p) =>
      !subscription ||
      p.displayOrder >
        (plans.find((pl) => pl.id === subscription.subscriptionPlanId)
          ?.displayOrder ?? 0)
  );

  const currentPlanDetail = currentPlan;

  const formatPrice = (price: number): string => {
    return price.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const getFeaturesByCategory = (plan: PlanListView) => {
    const grouped: Record<string, PlanListView['features']> = {};
    for (const f of plan.features) {
      const cat = f.category || 'other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(f);
    }
    return grouped;
  };

  const handleUpgrade = (planSlug: string) => {
    const cycle = billingCycle === 'annual' ? 'yearly' : 'monthly';
    navigate(`/subscription/checkout?businessId=${business.id}&plan=${planSlug}&cycle=${cycle}`);
  };

  if (isLoadingPlans) {
    return (
      <Box display='flex' flexDirection='column' gap={4}>
        <Typography variant='h4' fontWeight={700}>
          Meu Plano
        </Typography>
        <Box display='flex' justifyContent='center' py={6}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!subscription) {
    return (
      <Box display='flex' flexDirection='column' gap={4}>
        <Typography variant='h4' fontWeight={700}>
          Meu Plano
        </Typography>
        <Card sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <Icon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }}>
            credit_card_off
          </Icon>
          <Typography variant='h6' color='text.secondary' gutterBottom>
            Nenhum plano ativo
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Sua empresa ainda não possui uma assinatura. Entre em contato com o
            suporte.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box display='flex' flexDirection='column' gap={4}>
      <Typography variant='h4' fontWeight={700}>
        Meu Plano
      </Typography>

      {/* ── Current Plan ── */}
      {currentPlanDetail && (
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            border: `2px solid ${
              planColors[currentPlanDetail.slug] || theme.palette.primary.main
            }`,
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
          }}
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='flex-start'
            flexWrap='wrap'
            gap={2}
          >
            <Box>
              <Box display='flex' alignItems='center' gap={1.5} mb={1}>
                <Typography variant='h5' fontWeight={700}>
                  {currentPlanDetail.name}
                </Typography>
                {currentPlanDetail.isPopular && (
                  <Chip label='Mais Popular' size='small' color='primary' />
                )}
                <Chip
                  label={
                    subscription.isTrialing
                      ? 'Período de Teste'
                      : subscription.status === 'Active'
                        ? 'Ativo'
                        : subscription.status
                  }
                  size='small'
                  color={subscription.isTrialing ? 'warning' : 'success'}
                  variant='outlined'
                />
              </Box>
              <Typography
                variant='h4'
                fontWeight={700}
                color={planColors[currentPlanDetail.slug]}
              >
                {formatPrice(
                  subscription.billingCycle === 'Yearly'
                    ? currentPlanDetail.yearlyPrice
                    : currentPlanDetail.monthlyPrice
                )}
                <Typography
                  component='span'
                  variant='body2'
                  color='text.secondary'
                  sx={{ ml: 0.5 }}
                >
                  /
                  {subscription.billingCycle === 'Yearly' ? 'ano' : 'mês'}
                </Typography>
              </Typography>
              {currentPlanDetail.description && (
                <Typography variant='body2' color='text.secondary' mt={0.5}>
                  {currentPlanDetail.description}
                </Typography>
              )}
            </Box>

            <Box textAlign='right'>
              {subscription.trialEndsAt && (
                <Typography variant='body2' color='text.secondary'>
                  Trial até:{' '}
                  <strong>
                    {new Date(subscription.trialEndsAt).toLocaleDateString(
                      'pt-BR'
                    )}
                  </strong>
                </Typography>
              )}
              {subscription.expiresAt && (
                <Typography variant='body2' color='text.secondary'>
                  Expira em:{' '}
                  <strong>
                    {new Date(subscription.expiresAt).toLocaleDateString(
                      'pt-BR'
                    )}
                  </strong>
                </Typography>
              )}
              <Typography variant='body2' color='text.secondary'>
                Ciclo:{' '}
                <strong>
                  {subscription.billingCycle === 'Yearly' ? 'Anual' : 'Mensal'}
                </strong>
              </Typography>
            </Box>
          </Box>

          {/* Limits */}
          {currentPlanDetail.limits.length > 0 && (
            <Box mt={3}>
              <Typography variant='subtitle2' fontWeight={600} mb={1}>
                Limites do plano
              </Typography>
              <Grid container spacing={2}>
                {currentPlanDetail.limits.map((limit) => {
                  const labels: Record<string, string> = {
                    max_professionals: 'Profissionais',
                    max_admins: 'Administradores',
                    max_ai_interactions: 'Interações IA/mês',
                  };
                  const icons: Record<string, string> = {
                    max_professionals: 'people',
                    max_admins: 'engineering',
                    max_ai_interactions: 'smart_toy',
                  };
                  const displayValue =
                    limit.key === 'max_ai_interactions' &&
                    limit.value >= 2147483647
                      ? 'Ilimitado'
                      : String(limit.value);
                  return (
                    <Grid item xs={6} sm={3} key={limit.key}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: theme.palette.action.selected,
                          textAlign: 'center',
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: 28,
                            color: planColors[currentPlanDetail.slug],
                            mb: 0.5,
                          }}
                        >
                          {icons[limit.key] || 'check_circle'}
                        </Icon>
                        <Typography variant='h5' fontWeight={700}>
                          {displayValue}
                        </Typography>
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {labels[limit.key] || limit.key}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* Features */}
          <Box mt={3}>
            <Typography variant='subtitle2' fontWeight={600} mb={1}>
              Recursos inclusos
            </Typography>
            <Grid container spacing={1}>
              {Object.entries(
                getFeaturesByCategory(currentPlanDetail)
              ).map(([cat, features]) => (
                <Grid item xs={12} sm={6} key={cat}>
                  <Typography
                    variant='caption'
                    fontWeight={600}
                    color='text.secondary'
                    display='block'
                    mb={0.5}
                  >
                    {categoryLabels[cat] || cat}
                  </Typography>
                  {features.map((f) => (
                    <Box
                      key={f.code}
                      display='flex'
                      alignItems='center'
                      gap={0.5}
                      mb={0.5}
                    >
                      <Icon
                        sx={{
                          fontSize: 16,
                          color: f.enabled ? 'success.main' : 'text.disabled',
                        }}
                      >
                        {f.enabled
                          ? 'check_circle'
                          : 'remove_circle_outline'}
                      </Icon>
                      <Typography
                        variant='body2'
                        color={f.enabled ? 'text.primary' : 'text.disabled'}
                      >
                        {f.name}
                      </Typography>
                    </Box>
                  ))}
                </Grid>
              ))}
            </Grid>
          </Box>
        </Card>
      )}

      {/* ── Upgrade Options ── */}
      {upgradePlans && upgradePlans.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant='h5' fontWeight={600}>
            Faça upgrade do seu plano
          </Typography>

          {/* Billing Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <ToggleButtonGroup
              value={billingCycle}
              exclusive
              onChange={(_, newCycle) => newCycle && setBillingCycle(newCycle)}
              sx={{
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.04)',
                borderRadius: 3,
                p: 0.5,
                '& .MuiToggleButton-root': {
                  px: 3,
                  py: 1,
                  borderRadius: 2.5,
                  border: 'none',
                  textTransform: 'none',
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main,
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  },
                },
              }}
            >
              <ToggleButton value="monthly">Mensal</ToggleButton>
              <ToggleButton value="annual">
                Anual
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    fontSize: '0.7rem',
                    backgroundColor: theme.palette.success.main,
                    color: '#ffffff',
                    px: 0.8,
                    py: 0.2,
                    borderRadius: 1,
                    fontWeight: 700,
                  }}
                >
                  ECONOMIZE
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Grid container spacing={3}>
            {upgradePlans.map((plan) => {
              const color = planColors[plan.slug] || theme.palette.primary.main;
              const groupedFeatures = getFeaturesByCategory(plan);
              const monthlyPrice = formatPrice(plan.monthlyPrice);
              const yearlyPrice = formatPrice(plan.yearlyPrice);

              return (
                <Grid item xs={12} md={6} key={plan.id}>
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: plan.isPopular
                        ? `2px solid ${theme.palette.primary.main}`
                        : `1px solid ${theme.palette.divider}`,
                      position: 'relative',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[8],
                      },
                    }}
                  >
                    {plan.isPopular && (
                      <Chip
                        label='Mais Popular'
                        size='small'
                        color='primary'
                        sx={{ position: 'absolute', top: -10, right: 16 }}
                      />
                    )}

                    <Typography variant='h5' fontWeight={700} gutterBottom>
                      {plan.name}
                    </Typography>
                    <Typography variant='body2' color='text.secondary' mb={2}>
                      {plan.description}
                    </Typography>

                    <Box mb={2}>
                      <Typography variant='h4' fontWeight={700} color={color}>
                        {billingCycle === 'annual'
                          ? formatPrice(plan.yearlyPrice)
                          : monthlyPrice}
                        <Typography
                          component='span'
                          variant='body2'
                          color='text.secondary'
                          sx={{ ml: 0.5 }}
                        >
                          /{billingCycle === 'annual' ? 'ano' : 'mês'}
                        </Typography>
                      </Typography>
                      {billingCycle === 'monthly' && (
                        <Typography variant='body2' color='text.secondary'>
                          ou {yearlyPrice}/ano
                          {(plan.yearlyDiscountPercentage ?? 0) > 0 &&
                            ` (economia de ${Math.round(plan.yearlyDiscountPercentage ?? 0)}%)`}
                        </Typography>
                      )}
                      {billingCycle === 'annual' && plan.yearlyPrice > 0 && (
                        <Typography variant='body2' color='success.main' fontWeight={600}>
                          {Math.round(plan.yearlyDiscountPercentage ?? 0)}% de economia
                        </Typography>
                      )}
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Box flex={1}>
                      {Object.entries(groupedFeatures).map(
                        ([cat, features]) => (
                          <Box key={cat} mb={1.5}>
                            <Typography
                              variant='caption'
                              fontWeight={600}
                              color='text.secondary'
                              display='block'
                              mb={0.5}
                            >
                              {categoryLabels[cat] || cat}
                            </Typography>
                            {features.map((f) => (
                              <Box
                                key={f.code}
                                display='flex'
                                alignItems='center'
                                gap={0.5}
                                mb={0.25}
                              >
                                <Icon
                                  sx={{ fontSize: 14, color: 'success.main' }}
                                >
                                  check
                                </Icon>
                                <Typography variant='body2'>
                                  {f.name}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )
                      )}
                    </Box>

                    <Button
                      variant='contained'
                      fullWidth
                      size='large'
                      sx={{
                        mt: 2,
                        bgcolor: color,
                        '&:hover': {
                          bgcolor: color,
                          filter: 'brightness(0.9)',
                        },
                      }}
                      onClick={() => handleUpgrade(plan.slug)}
                    >
                      {subscription ? 'Fazer Upgrade' : 'Contratar'}
                    </Button>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {upgradePlans?.length === 0 && (
        <Box textAlign='center' py={4}>
          <Icon sx={{ fontSize: 48, color: 'success.main', mb: 1 }}>star</Icon>
          <Typography variant='h6' color='success.main'>
            Você está no melhor plano!
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Aproveite todos os recursos do {currentPlanDetail?.name}.
          </Typography>
        </Box>
      )}
    </Box>
  );
};
